/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ColumnFiltersState, SortingState, VisibilityState } from '@tanstack/react-table'
import { Company } from 'types/entities/Company'
import { CompanyBranchType } from 'types/enums/CompanyBranchType'
import { CompaniesFilterType } from 'types/enums/CompaniesFilterType'

type TableSearchScalar = string | number | boolean | null
type TableSearch =
    | TableSearchScalar
    | TableSearchScalar[]
    | Record<string, TableSearchScalar | TableSearchScalar[]>

export type XlsxSampleCell = string | number | boolean | null

interface CompaniesTableUiState {
    pageIndex: number
    pageSize: number
    globalFilter: string
    columnFilters: ColumnFiltersState
    sorting: SortingState
    view: CompaniesFilterType | 'all'
    columnVisibility?: VisibilityState
}

interface CompaniesTableMetaState {
    currentPage: number
    perPage: number
    total: number
    sortBy: string | null
    sort: 'asc' | 'desc' | null
    search: TableSearch
}

interface CompaniesTableState {
    ui: CompaniesTableUiState
    meta: CompaniesTableMetaState | null
    hasLoaded: boolean
}

export interface CompaniesState {
    all: Company[]
    parents: Company[]
    branches: Company[]
    selectedCompany: Company | null
    table: CompaniesTableState
    xlsxImportDraft: XlsxDraftState | null
}

export type XlsxDraftColumn = {
    originalHeader: string
    displayHeader: string
    index: number
}

export type XlsxDraftMapping = {
    columnHeader: string
    mappedCategory: string | null
    mappedFieldKey: string | null
    mappedIgnore?: boolean
}

export type XlsxDraftFile = {
    name: string
    type: string
    data: string
}

export type XlsxDraftState = {
    file: XlsxDraftFile | null
    columns: XlsxDraftColumn[]
    mapping: XlsxDraftMapping[]
    rowsSample: Record<string, XlsxSampleCell>[]
}

const initialState: CompaniesState = {
    all: [],
    parents: [],
    branches: [],
    selectedCompany: null,
    table: {
        ui: {
            pageIndex: 0,
            pageSize: 50,
            globalFilter: '',
            columnFilters: [],
            sorting: [{ id: 'id', desc: true }],
            view: 'all',
        },
        meta: null,
        hasLoaded: false,
    },
    xlsxImportDraft: null,
}

const companiesSlice = createSlice({
    name: 'companies',
    initialState,
    reducers: {
        setCompanies(state, action: PayloadAction<Company[]>) {
            const companies = action.payload ?? []
            const parentCompanies = companies.filter(
                (c) => c.type === CompanyBranchType.Parent
            )
            const branchCompanies = companies.filter(
                (c) => c.type === CompanyBranchType.Branch
            )

            state.all = companies
            state.parents = parentCompanies
            state.branches = branchCompanies
        },
        pushCompany: {
            prepare: (payload: Company, created = false) => ({payload, meta: {created}}),
            reducer(state, action: PayloadAction<Company, string, {created: boolean}>) {
                const company = action.payload
                const index = state.all.findIndex((c) => c.id === company.id)

                if (index === -1) {
                    state.all.push(company)
                    if (action.meta.created && state.table.meta) state.table.meta.total += 1

                    if (company.type === CompanyBranchType.Parent) {
                        state.parents.push(company)
                    } else if (company.type === CompanyBranchType.Branch) {
                        state.branches.push(company)
                    }
                }

                if (index != -1) {
                    state.all[index] = company

                    if (company.type === CompanyBranchType.Parent) {
                        const parentIndex = state.parents.findIndex(
                            (c) => c.id === company.id
                        )
                        if (parentIndex !== -1) {
                            state.parents[parentIndex] = company
                        }
                    } else if (company.type === CompanyBranchType.Branch) {
                        const branchIndex = state.branches.findIndex(
                            (c) => c.id === company.id
                        )
                        if (branchIndex !== -1) {
                            state.branches[branchIndex] = company
                        }
                    }
                }
            },
        },

        deleteCompany(state, action: PayloadAction<number>) {
            const companyId = action.payload
            state.all = state.all.filter((c) => c.id !== companyId)
            state.parents = state.parents.filter((c) => c.id !== companyId)
            state.branches = state.branches.filter((c) => c.id !== companyId)

            if (state.selectedCompany?.id === companyId) {
                state.selectedCompany = null
            }
        },
        setSelectedCompany(state, action: PayloadAction<Company | null>) {
            state.selectedCompany = action.payload
        },
        clearCompanies(state) {
            state.all = []
            state.parents = []
            state.branches = []
            state.selectedCompany = null
            state.table = {
                ui: { ...initialState.table.ui },
                meta: null,
                hasLoaded: false,
            }
            state.xlsxImportDraft = null
        },
        setCompaniesTableUiState(
            state,
            action: PayloadAction<Partial<CompaniesTableUiState>>
        ) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                    hasLoaded: false,
                }
            }
            if (!state.table.ui) {
                state.table.ui = { ...initialState.table.ui }
            }
            state.table.ui = {
                ...state.table.ui,
                ...action.payload,
            }
        },
        setCompaniesTableMeta(
            state,
            action: PayloadAction<CompaniesTableMetaState>
        ) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                    hasLoaded: false,
                }
            }
            state.table.meta = action.payload
            state.table.hasLoaded = true
        },
        setXlsxImportDraft(state, action: PayloadAction<XlsxDraftState>) {
            state.xlsxImportDraft = action.payload
        },
        clearXlsxImportDraft(state) {
            state.xlsxImportDraft = null
        },
        upsertSelectedCompanyContact(
            state,
            action: PayloadAction<{
                contact: import('types/entities/CompanyContact').CompanyContact
            }>
        ) {
            const contact = action.payload.contact
            if (!state.selectedCompany || !contact) {
                return
            }
            const companyId = state.selectedCompany.id

            const currentContacts = state.selectedCompany.contacts ?? []
            const idx = currentContacts.findIndex((c) => c.id === contact.id)
            if (idx === -1) {
                state.selectedCompany.contacts = [...currentContacts, contact]
            } else {
                const next = [...currentContacts]
                next[idx] = contact
                state.selectedCompany.contacts = next
            }
            const allIdx = state.all.findIndex((c) => c.id === companyId)
            if (allIdx !== -1) {
                const base = state.all[allIdx]
                const baseContacts = base.contacts ?? []
                const bIdx = baseContacts.findIndex((c) => c.id === contact.id)
                if (bIdx === -1) {
                    state.all[allIdx] = {
                        ...base,
                        contacts: [...baseContacts, contact],
                    }
                } else {
                    const updated = [...baseContacts]
                    updated[bIdx] = contact
                    state.all[allIdx] = { ...base, contacts: updated }
                }
            }
        },
        removeSelectedCompanyContact(
            state,
            action: PayloadAction<{ contactId: number }>
        ) {
            const contactId = action.payload.contactId
            if (!state.selectedCompany) {
                return
            }
            const companyId = state.selectedCompany.id
            const currentContacts = state.selectedCompany.contacts ?? []
            state.selectedCompany.contacts = currentContacts.filter(
                (c) => c.id !== contactId
            )
            const allIdx = state.all.findIndex((c) => c.id === companyId)
            if (allIdx !== -1) {
                const base = state.all[allIdx]
                const baseContacts = base.contacts ?? []
                state.all[allIdx] = {
                    ...base,
                    contacts: baseContacts.filter((c) => c.id !== contactId),
                }
            }
        },
        upsertSelectedCompanyAddress(
            state,
            action: PayloadAction<{
                address: import('types/entities/CompanyAddress').CompanyAddress
            }>
        ) {
            const address = action.payload.address
            if (!state.selectedCompany) {
                return
            }
            const companyId = state.selectedCompany.id
            const currentAddresses = state.selectedCompany.addresses ?? []
            const idx = currentAddresses.findIndex((a) => a.id === address.id)
            if (idx === -1) {
                state.selectedCompany.addresses = [...currentAddresses, address]
            } else {
                const next = [...currentAddresses]
                next[idx] = address
                state.selectedCompany.addresses = next
            }
            const allIdx = state.all.findIndex((c) => c.id === companyId)
            if (allIdx !== -1) {
                const base = state.all[allIdx]
                const baseAddresses = base.addresses ?? []
                const bIdx = baseAddresses.findIndex((a) => a.id === address.id)
                if (bIdx === -1) {
                    state.all[allIdx] = {
                        ...base,
                        addresses: [...baseAddresses, address],
                    }
                } else {
                    const updated = [...baseAddresses]
                    updated[bIdx] = address
                    state.all[allIdx] = { ...base, addresses: updated }
                }
            }
        },
        removeSelectedCompanyAddress(
            state,
            action: PayloadAction<{ addressId: number }>
        ) {
            const addressId = action.payload.addressId
            if (!state.selectedCompany) {
                return
            }
            const companyId = state.selectedCompany.id
            const currentAddresses = state.selectedCompany.addresses ?? []
            state.selectedCompany.addresses = currentAddresses.filter(
                (a) => a.id !== addressId
            )
            const allIdx = state.all.findIndex((c) => c.id === companyId)
            if (allIdx !== -1) {
                const base = state.all[allIdx]
                const baseAddresses = base.addresses ?? []
                state.all[allIdx] = {
                    ...base,
                    addresses: baseAddresses.filter((a) => a.id !== addressId),
                }
            }
        },
        upsertSelectedCompanyOffer(
            state,
            action: PayloadAction<{
                offer: import('types/entities/Offer').Offer
            }>
        ) {
            const offer = action.payload.offer
            if (!state.selectedCompany || !offer) {
                return
            }

            const companyId = state.selectedCompany.id
            const currentOffers = state.selectedCompany.offers ?? []
            const idx = currentOffers.findIndex((o) => o.id === offer.id)

            if (idx === -1) {
                state.selectedCompany.offers = [...currentOffers, offer]
            } else {
                const next = [...currentOffers]
                next[idx] = offer
                state.selectedCompany.offers = next
            }

            const allIdx = state.all.findIndex((c) => c.id === companyId)
            if (allIdx !== -1) {
                const base = state.all[allIdx]
                const baseOffers = base.offers ?? []
                const bIdx = baseOffers.findIndex((o) => o.id === offer.id)

                if (bIdx === -1) {
                    state.all[allIdx] = {
                        ...base,
                        offers: [...baseOffers, offer],
                    }
                } else {
                    const updated = [...baseOffers]
                    updated[bIdx] = offer
                    state.all[allIdx] = {
                        ...base,
                        offers: updated,
                    }
                }
            }
        },
        upsertSelectedCompanyLead(
            state,
            action: PayloadAction<{
                lead: import('types/entities/Lead').Lead
            }>
        ) {
            const lead = action.payload.lead
            if (!state.selectedCompany || !lead) {
                return
            }

            const companyId = state.selectedCompany.id
            const currentLeads = state.selectedCompany.leads ?? []
            const idx = currentLeads.findIndex((l) => l.id === lead.id)

            if (idx === -1) {
                state.selectedCompany.leads = [...currentLeads, lead]
            } else {
                const next = [...currentLeads]
                next[idx] = lead
                state.selectedCompany.leads = next
            }

            const allIdx = state.all.findIndex((c) => c.id === companyId)
            if (allIdx !== -1) {
                const base = state.all[allIdx]
                const baseLeads = base.leads ?? []
                const bIdx = baseLeads.findIndex((l) => l.id === lead.id)

                if (bIdx === -1) {
                    state.all[allIdx] = {
                        ...base,
                        leads: [...baseLeads, lead],
                    }
                } else {
                    const updated = [...baseLeads]
                    updated[bIdx] = lead
                    state.all[allIdx] = {
                        ...base,
                        leads: updated,
                    }
                }
            }
        },
        resetState: () => initialState,
    },
})

export const {
    setCompanies,
    pushCompany,
    deleteCompany,
    setSelectedCompany,
    clearCompanies,
    setCompaniesTableUiState,
    setCompaniesTableMeta,
    setXlsxImportDraft,
    clearXlsxImportDraft,
    upsertSelectedCompanyContact,
    removeSelectedCompanyContact,
    upsertSelectedCompanyAddress,
    removeSelectedCompanyAddress,
    upsertSelectedCompanyOffer,
    upsertSelectedCompanyLead,
    resetState,
} = companiesSlice.actions

export default companiesSlice.reducer
